import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import "./tailwind.css";
import "./index.css";
import "./pages/auth/SignInPage.css";
import AppRoutes from "./AppRoutes.jsx";
import { AuthProvider } from "./context/AuthContext.jsx";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary.jsx";
import SiteDomainBoundary from "./routing/SiteDomainBoundary.jsx";
import {
  isAdminPathname,
  trackEvent,
  trackGoogleAdsConversion,
  trackMetaEvent,
  trackPageView,
} from "./utils/analytics.js";

function patchReactElementFactory() {
  if (!React?.createElement || React.createElement.__offlicoPatched) return;
  const isInvalidRef = (ref) =>
    ref != null && typeof ref !== "function" && typeof ref !== "object";
  const stripInvalidRef = (props) => {
    if (!props || typeof props !== "object" || !("ref" in props)) return props;
    if (!isInvalidRef(props.ref)) return props;
    const { ref: _ref, ...rest } = props;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[offlico] Dropped invalid ref in createElement", { ref: props.ref });
    }
    return rest;
  };
  const originalCreateElement = React.createElement;
  React.createElement = function patchedCreateElement(type, props, ...children) {
    return originalCreateElement(type, stripInvalidRef(props), ...children);
  };
  const originalCloneElement = React.cloneElement;
  if (typeof originalCloneElement === "function") {
    React.cloneElement = function patchedCloneElement(element, props, ...children) {
      return originalCloneElement(element, stripInvalidRef(props), ...children);
    };
  }
  React.createElement.__offlicoPatched = true;
}

patchReactElementFactory();

function reloadForFreshBuild(reason) {
  if (!import.meta.env.PROD) return;
  const key = "offlico_reload_for_fresh_build";
  const lastReload = Number(sessionStorage.getItem(key) || "0");
  if (Date.now() - lastReload < 10000) return;
  sessionStorage.setItem(key, String(Date.now()));
  console.info(`[offlico] Reloading for fresh build: ${reason}`);
  window.location.reload();
}

function installStaleAssetRecovery() {
  if (!import.meta.env.PROD || typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadForFreshBuild("preload-error");
  });

  window.addEventListener("error", (event) => {
    const message = String(event?.message || event?.error?.message || "");
    if (
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Importing a module script failed") ||
      message.includes("ChunkLoadError")
    ) {
      reloadForFreshBuild("stale-asset-error");
    }
  });
}

installStaleAssetRecovery();

async function ensureFreshBuild() {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return false;
    const data = await res.json();
    const version = data?.git_sha || data?.build_id || data?.version;
    if (!version) return false;
    const currentBuild = window.__OFFLICO_BUILD_ID__ || "";
    const key = "offlico_build_version";
    const prev = localStorage.getItem(key);
    const staleCurrentBuild = currentBuild && !String(version).startsWith(currentBuild);
    if (staleCurrentBuild || (prev && prev !== version)) {
      localStorage.setItem(key, version);
      reloadForFreshBuild("version-change");
      return true;
    }
    if (!prev) {
      localStorage.setItem(key, version);
    }
  } catch {
    // Ignore version check failures and allow the app to load.
  }
  return false;
}

function scheduleFreshBuildCheck() {
  if (!import.meta.env.PROD) return;
  const run = () => {
    void ensureFreshBuild();
  };
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 1500 });
    return;
  }
  window.setTimeout(run, 0);
}

function RootRouter() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* Offlico's addresses (/app, /book/<slug>, /pricing ...) go to
          https://www.offlico.com before anything renders. */}
      <SiteDomainBoundary>
        <AuthProvider>
          <AnalyticsTracker />
          <AppRoutes />
        </AuthProvider>
      </SiteDomainBoundary>
    </BrowserRouter>
  );
}

function AnalyticsTracker() {
  const location = useLocation();

  React.useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;
    if (globalThis.__offlico_last_tracked_path === path) return undefined;
    globalThis.__offlico_last_tracked_path = path;

    const frame = window.requestAnimationFrame(() => {
      trackPageView({ path });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search, location.hash]);

  React.useEffect(() => {
    const handleClick = (event) => {
      if (isAdminPathname(window.location.pathname)) {
        return;
      }

      const element = event.target instanceof Element ? event.target.closest("a,button") : null;
      if (!element) return;

      const label = element.getAttribute("aria-label")?.trim() || element.textContent?.trim() || "";
      const normalizedLabel = label.toLowerCase();

      if (element instanceof HTMLAnchorElement) {
        const href = element.getAttribute("href") || "";

        if (href.startsWith("mailto:")) {
          trackEvent("email_click", {
            email_address: href.replace(/^mailto:/, ""),
            link_text: label || "email",
          });
          trackGoogleAdsConversion("professional_contact_click", {
            contact_method: "email",
            link_text: label || "email",
          });
          return;
        }

        if (href.startsWith("tel:")) {
          trackEvent("phone_click", {
            link_text: label || "phone",
          });
          trackGoogleAdsConversion("professional_contact_click", {
            contact_method: "phone",
            link_text: label || "phone",
          });
          return;
        }

        const url = new URL(href, window.location.origin);
        const isSignupIntent =
          url.pathname === "/upgrade" ||
          url.pathname === "/signup" ||
          (url.pathname === "/signin" && url.searchParams.get("mode") === "signup");

        if (url.pathname.startsWith("/book") || normalizedLabel.includes("book now") || normalizedLabel.includes("book online")) {
          trackEvent("booking_intent", {
            link_text: label || "booking",
            link_path: url.pathname,
          });
          trackGoogleAdsConversion("booking_intent", {
            link_text: label || "booking",
            link_path: url.pathname,
          });
          return;
        }

        if (
          isSignupIntent ||
          normalizedLabel.includes("get early access")
        ) {
          const metaLeadPayload = {
            content_name: "Offlico Professional CTA",
            content_category: "signup",
            value: 0,
            currency: "GBP",
          };

          trackEvent("professional_upgrade_click", {
            cta_text: label || "Professional",
            link_path: url.pathname,
            redirect_path: url.searchParams.get("redirect") || "",
          });

          trackMetaEvent("Lead", metaLeadPayload);
        }
        return;
      }

      if (element instanceof HTMLButtonElement && normalizedLabel.includes("see how it works")) {
        trackEvent("see_how_it_works_click", {
          button_text: label || "see how it works",
        });
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  return null;
}

const container = document.getElementById("root");
if (container) {
  const existingRoot = globalThis.__offlico_root;
  const renderApp = (root) => {
    root.render(
      <React.StrictMode>
        <AppErrorBoundary>
          <RootRouter />
        </AppErrorBoundary>
      </React.StrictMode>
    );
  };

  const start = async () => {
    const root = existingRoot || ReactDOM.createRoot(container);
    globalThis.__offlico_root = root;
    renderApp(root);
    scheduleFreshBuildCheck();
  };

  start();
}
